import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useState } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import { toastShown } from '@/features/ui';

import { useAddCardMutation } from './payments.api';
import { elementStyle, stripePromise } from './stripe';
import { StripeField } from './StripeField';

interface AddCardDialogProps {
  open: boolean;
  onClose: () => void;
  /** True when the account has no cards yet — the first card becomes the
   *  default whatever the checkbox says, so the checkbox is not offered. */
  isFirstCard: boolean;
}

/**
 * Adds a card through Stripe Elements.
 *
 * Card details go straight from the iframe to Stripe and never touch this
 * application or its server — what the API receives is a single-use token. That
 * is the whole reason for Elements, and why there is no card-number input here.
 */
export const AddCardDialog = ({ open, onClose, isFirstCard }: AddCardDialogProps) => (
  // Elements must wrap the form, and remounting per open is what clears the
  // iframes — Stripe gives no imperative reset.
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
    <Elements stripe={stripePromise}>
      <AddCardForm onClose={onClose} isFirstCard={isFirstCard} />
    </Elements>
  </Dialog>
);

interface AddCardFormProps {
  onClose: () => void;
  isFirstCard: boolean;
}

const AddCardForm = ({ onClose, isFirstCard }: AddCardFormProps) => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const [addCard] = useAddCardMutation();

  const [cardholderName, setCardholderName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const setFieldError = (field: string, message: string) => {
    setFieldErrors((current) => ({ ...current, [field]: message }));
  };

  const isReady = stripe !== null && elements !== null;

  const handleSubmit = async () => {
    const cardNumber = elements?.getElement(CardNumberElement);
    if (!stripe || !cardNumber) return;

    setIsSubmitting(true);
    setFormError('');

    // Tokenise first: a token is what the API accepts, and a card that Stripe
    // rejects must never reach it.
    const { token, error } = await stripe.createToken(cardNumber, {
      name: cardholderName.trim(),
    });

    // Stripe's result is a discriminated union: no token means an error, so
    // checking the token is what narrows both halves.
    if (token === undefined) {
      setFormError(error.message ?? 'That card could not be verified. Check the details.');
      setIsSubmitting(false);
      return;
    }

    try {
      await addCard({ cardToken: token.id, isDefault: isFirstCard || makeDefault }).unwrap();
      dispatch(toastShown('Card saved.', 'success'));
      onClose();
    } catch {
      setFormError('Your card could not be saved. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <DialogTitle>Add a card</DialogTitle>

      <DialogContent className="flex flex-col gap-4">
        {formError !== '' && (
          <Alert severity="error" role="alert" className="rounded-lg">
            {formError}
          </Alert>
        )}

        <TextField
          className="mt-2"
          label="Name on card"
          size="small"
          value={cardholderName}
          onChange={(event) => {
            setCardholderName(event.target.value);
          }}
          autoComplete="cc-name"
          placeholder="John Smith"
          // The dialog opens on a tap, so the field is not autofocused: on iOS
          // that raises the keyboard over the card fields before they are seen.
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <StripeField label="Card number" htmlFor="card-number" error={fieldErrors.number}>
          {(handlers) => (
            <CardNumberElement
              id="card-number"
              options={{ style: elementStyle, showIcon: true }}
              onChange={(event) => {
                setFieldError('number', event.error?.message ?? '');
              }}
              {...handlers}
            />
          )}
        </StripeField>

        {/* Expiry and CVC share a row from the smallest width: they are short
            fields, and stacking them pushes the submit button off a phone. */}
        <div className="flex gap-3">
          <StripeField
            label="Expiry"
            htmlFor="card-expiry"
            error={fieldErrors.expiry}
            className="flex-1"
          >
            {(handlers) => (
              <CardExpiryElement
                id="card-expiry"
                options={{ style: elementStyle }}
                onChange={(event) => {
                  setFieldError('expiry', event.error?.message ?? '');
                }}
                {...handlers}
              />
            )}
          </StripeField>

          <StripeField
            label="CVC"
            htmlFor="card-cvc"
            error={fieldErrors.cvc}
            className="flex-1"
          >
            {(handlers) => (
              <CardCvcElement
                id="card-cvc"
                options={{ style: elementStyle }}
                onChange={(event) => {
                  setFieldError('cvc', event.error?.message ?? '');
                }}
                {...handlers}
              />
            )}
          </StripeField>
        </div>

        {isFirstCard ? (
          <Alert severity="info" className="rounded-lg">
            This will become your default card.
          </Alert>
        ) : (
          <FormControlLabel
            control={
              <Checkbox
                checked={makeDefault}
                onChange={(event) => {
                  setMakeDefault(event.target.checked);
                }}
              />
            }
            label="Make this my default card"
          />
        )}
      </DialogContent>

      <DialogActions className="flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end">
        <Button onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!isReady || isSubmitting}
          className="w-full sm:w-auto"
        >
          {isSubmitting ? 'Saving…' : 'Save card'}
        </Button>
      </DialogActions>
    </form>
  );
};
